import React, { useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import {
  Arrow,
  CenteredText,
  TooltipFrame,
  TooltipVariant,
} from './Tooltip.styles';

export interface TooltipProps {
  text?: string;
  variant: TooltipVariant;
  root: HTMLElement;
  onClick?: () => void;
  children?: React.ReactNode;
  width?: string;
  className?: string;
  removePadding?: boolean;
  maxWidth?: string;
  extraPaddingForRoot?: number;
  lineBreak?: 'anywhere' | 'normal';
}

const variantMap = {
  up: 'bottom',
  down: 'top',
  left: 'right',
  right: 'left',
} as const;

const reverseVariantMap = {
  bottom: 'up',
  top: 'down',
  right: 'left',
  left: 'right',
} as const;

export const Tooltip = ({
  text,
  variant,
  root,
  children,
  ...props
}: TooltipProps) => {
  const tooltipElement = useRef<HTMLDivElement>(null);
  const [arrow, setArrow] = useState<HTMLDivElement | null>(null);

  const {
    styles,
    attributes,
    state: popperState,
  } = usePopper(root, tooltipElement.current, {
    modifiers: [
      { name: 'arrow', options: { element: arrow } },
      {
        name: 'offset',
        options: {
          offset: [0, 7.5],
        },
      },
    ],
    placement: variantMap[variant],
  });

  const popperStateMatchesVariant =
    popperState?.placement === variantMap[variant];
  const tooltipVariant =
    !popperStateMatchesVariant && popperState?.placement
      ? (reverseVariantMap[
          popperState.placement as keyof typeof reverseVariantMap
        ] as TooltipVariant)
      : variant;

  const tooltip = (
    <TooltipFrame
      ref={tooltipElement}
      style={styles.popper}
      variant={tooltipVariant}
      {...attributes.popper}
      data-testid={'tooltip-frame'}
      {...props}
    >
      {text && <CenteredText>{text}</CenteredText>}
      {children}
      <Arrow
        variant={tooltipVariant}
        ref={setArrow}
        style={styles.arrow}
        data-testid={`${variant}-arrow`}
      />
    </TooltipFrame>
  );

  return tooltip;
};
